import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Input, message, Radio, Tag, TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/lang/isEqual';
import { CsTreeSelect } from 'components/antd-extension/CsTreeSelect';
import { DownOutlined } from '@ant-design/icons';
import { isNullish } from 'utils/nullish';
import uniq from 'lodash/array/uniq';
import { VersionUtil } from 'utils/version-util';
import { createVersionSearchIndex, matchesVersionSearch, VERSION_SEARCH_LIMIT } from 'utils/version-wildcard';
import { ze } from 'utils/zhEn';

// 下拉底部「快捷选择别名版本」最多展示几个版本（按版本号倒序取）
const TOP_ALIASED_VERSIONS_LIMIT = 5;
const SEARCH_CAP_KEY = '__version_search_cap__';

function collectVersionSearchEntries(treeData) {
  const entries = [];
  const byKey = new Map();
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (!node || node.key == null || node.key === SEARCH_CAP_KEY) {
        continue;
      }
      byKey.set(node.key, node);
      entries.push({ text: String(node.key), nodeKey: node.key });
      if (node.versionAlias) {
        entries.push({ text: String(node.versionAlias), nodeKey: node.key });
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(treeData);
  return { entries, byKey };
}

function shallowMatchNode(node) {
  return {
    title: node.title,
    value: node.value,
    key: node.key,
    versionAlias: node.versionAlias,
  };
}

/**
 * 递归遍历 treeData，收集所有「叶子节点 + 有 versionAlias」的节点。
 */
function collectAliasedLeafNodes(treeData) {
  const result = [];
  function walk(nodes) {
    for (const node of nodes || []) {
      if (node.children?.length > 0) {
        walk(node.children);
      } else if (node.versionAlias) {
        result.push(node);
      }
    }
  }
  walk(treeData);
  return result;
}

function findNodeFromTreeData(treeData, key) {
  if (!treeData) {
    return null;
  }
  for (let i = 0; i < treeData.length; i++) {
    if (treeData[i].key === key) {
      return treeData[i];
    }
    if (treeData[i].children?.length > 0) {
      const foundNode = findNodeFromTreeData(treeData[i].children, key);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null;
}

/**
 * 节点是否命中搜索文本：版本号(key) 或 别名(versionAlias) 任一命中即可。
 * 不含 * 时是大小写不敏感子串。含 * 时整串通配，* 可在任意位置，.* 与 * 同义。
 * 父节点 / 通配符节点没有 versionAlias，等价于只看 key。
 */
function nodeMatchesSearch(treeNode, searchTextLower) {
  if (!searchTextLower) {
    return true;
  }
  if (matchesVersionSearch(treeNode.key, searchTextLower)) {
    return true;
  }
  if (matchesVersionSearch(treeNode.versionAlias, searchTextLower)) {
    return true;
  }
  return false;
}

function findMatchedChildNodeList(treeNode, searchText) {
  if (!treeNode) {
    return [];
  }
  // 大小写不敏感的匹配，与 antd TreeSelect 的默认过滤行为保持一致
  const searchTextLower = searchText?.toLowerCase() || '';
  if (nodeMatchesSearch(treeNode, searchTextLower)) {
    return [treeNode];
  }
  if (!treeNode.children?.length) {
    return [];
  }
  const allChildrenMatches = treeNode.children.every(x => nodeMatchesSearch(x, searchTextLower));
  if (allChildrenMatches) {
    return [treeNode];
  } else {
    const allDescendantMatches = treeNode.children
      .map(x => findMatchedChildNodeList(x, searchText))
      .flat();
    const isDescendantsSameAsChildren = treeNode.children.length === allDescendantMatches.length
      && treeNode.children.every((x, i) => x.key === allDescendantMatches[i].key);
    if (isDescendantsSameAsChildren) {
      return [treeNode];
    } else {
      return allDescendantMatches;
    }
  }
}

function getKeyListFromTreeDataFilteredBySearchText(treeData, checkedKey, searchText) {
  const checkedNode = findNodeFromTreeData(treeData, checkedKey);
  if (!checkedNode) {
    return [];
  }
  // console.log('gomo checkedNode = ', checkedNode);
  const nodeList = findMatchedChildNodeList(checkedNode, searchText);
  const keyList = nodeList.map(x => x.key);
  // console.log('gomo result keyList = ', keyList);
  return keyList;
}

const VersionHybridSelect = (props) => {
  const { t } = useTranslation();
  const {
    singleSelectMode: nullableSingleSelectMode,
    treeData: nullableTreeData,
    value: nullableValue,
    style,
    size,
    selectComponentSetting,
  } = props;

  const singleSelectMode = !!nullableSingleSelectMode;

  const treeData = nullableTreeData || [];
  const value = singleSelectMode ? nullableValue : (nullableValue || []);

  let {
    onChange,
    onDropdownVisibleChange,
    onChangeWhenDropdownClosed,
  } = props;

  onChange = onChange || (() => {});
  onDropdownVisibleChange = onDropdownVisibleChange || (() => {});
  onChangeWhenDropdownClosed = onChangeWhenDropdownClosed || (() => {});

  const [treeSelectSearchText, setTreeSelectSearchText] = useState('');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [intermediateValue, setIntermediateValue] = useState(undefined);

  useEffect(() => {
    if (!isEqual(value, intermediateValue)) {
      setIntermediateValue(value);
    }
  }, [value]);

  /**
   * 「快捷选择别名版本」需要用到的 top N 别名叶子节点。
   * 计算过程要遍历整棵 treeData，所以用 useMemo 缓存。
   * 排序：按版本号倒序（VersionUtil.compareVersion），取前 TOP_ALIASED_VERSIONS_LIMIT 个。
   */
  const topAliasedLeafNodes = useMemo(() => {
    const allAliasedLeaves = collectAliasedLeafNodes(treeData);
    return allAliasedLeaves
      .sort((a, b) => VersionUtil.compareVersion(b.value, a.value))
      .slice(0, TOP_ALIASED_VERSIONS_LIMIT);
  }, [treeData]);

  const versionSearchIndex = useMemo(() => {
    const collected = collectVersionSearchEntries(treeData);
    return {
      index: createVersionSearchIndex(collected.entries),
      byKey: collected.byKey,
    };
  }, [treeData]);

  const searchResult = useMemo(() => {
    const text = treeSelectSearchText || '';
    if (!text) {
      return null;
    }
    const found = versionSearchIndex.index.search(text, VERSION_SEARCH_LIMIT);
    const nodes = [];
    for (const key of found.keys) {
      const node = versionSearchIndex.byKey.get(key);
      if (node) {
        nodes.push(shallowMatchNode(node));
      }
    }
    if (found.capped) {
      nodes.push({
        title: ze(
          `只列出前 ${VERSION_SEARCH_LIMIT} 条，请把条件写得更具体`,
          `Only the first ${VERSION_SEARCH_LIMIT} matches. Type a longer pattern.`,
        ),
        value: SEARCH_CAP_KEY,
        key: SEARCH_CAP_KEY,
        disabled: true,
        disableCheckbox: true,
        selectable: false,
      });
    }
    return nodes;
  }, [treeSelectSearchText, versionSearchIndex]);

  /**
   * 点击底部快捷选择别名 Tag 时调用。
   * - 单选：直接覆盖 value
   * - 多选：already in value 则 no-op；不在则 push 到末尾
   * 复用现有 onChange / intermediate 通道：dropdown 打开时只更新 intermediateValue，
   * 关闭时由 onChangeWhenDropdownClosed 提交（虽然 footer 只在 dropdown 打开时可见，
   * 这里仍按原 onChange 处理函数的对称写法兜底）。
   */
  function handleAliasQuickPick(node) {
    const targetValue = node.value;
    let newV;
    if (singleSelectMode) {
      newV = targetValue;
    } else {
      const current = intermediateValue || [];
      if (current.includes(targetValue)) {
        return; // 已存在 → no-op
      }
      newV = [...current, targetValue];
    }
    if (dropdownOpen) {
      setIntermediateValue(newV);
    } else {
      onChangeWhenDropdownClosed(newV);
    }
    onChange(newV);
  }

  /**
   * antd的TreeSelect，如果输入了搜索内容，点击亲节点的时候，效果也是选中整颗树的所有节点。
   * 现在需求是输入了过滤内容，选择某个亲节点，只选中亲节点下面所有满足过滤条件的节点
   * */
  function handleChangeValue(oldValue, newValueWithoutFilter, extra, searchText) {
    oldValue = oldValue || [];
    if (isNullish(extra?.checked) || isNullish(extra?.triggerValue)) {
      console.error('antd TreeSelect, invalid extra params!');
      return newValueWithoutFilter;
    }
    if (!(searchText || '').trim()) {
      return newValueWithoutFilter;
    }
    const { triggerValue } = extra;
    if (triggerValue === SEARCH_CAP_KEY) {
      return oldValue;
    }
    if (!triggerValue.endsWith(VersionUtil.WILDCARD_CHARACTER)) { // 选择的版本不带通配符，就不需要考虑过滤逻辑
      return newValueWithoutFilter;
    }
    // 如果输入了搜索内容，如果选择的是亲节点，需要遍历一遍选项树，只选出包含过滤内容的节点
    const selectedKeyList = getKeyListFromTreeDataFilteredBySearchText(treeData, triggerValue, searchText);
    // console.log('gomo, old = ', oldValue, 'selected = ', selectedKeyList);
    // 判断是要选择还是取消选择，如果keyList都在oldValue中，就取消选择，否则就选择
    const oldValueSet = new Set(oldValue);
    const isRemoveNotAdd = selectedKeyList.every(x => oldValueSet.has(x));
    if (isRemoveNotAdd) {
      const selectedKeySet = new Set(selectedKeyList);
      return oldValue.filter(x => !selectedKeySet.has(x));
    } else {
      // selectedKeyList里面可能含有通配符，需要一个一个检查
      const oldValueWithoutDup = oldValue.filter(old => {
        return selectedKeyList.some(sel => {
          if (sel.endsWith(VersionUtil.WILDCARD_CHARACTER)) {
            return !old.startsWith(sel.substring(0, sel.length - VersionUtil.WILDCARD_CHARACTER.length));
          } else {
            return old !== sel;
          }
        });
      });
      // console.log('gomo, old = ', oldValue, 'selected = ', selectedKeyList, 'oldValueWithoutDup = ', oldValueWithoutDup);
      return [...oldValueWithoutDup, ...selectedKeyList];
    }
  }

  return <CsTreeSelect
    className="version-wildcard-search"
    size={size}
    style={style}
    showSearch={true}
    allowClear={true}
    treeData={searchResult == null ? treeData : searchResult}
    treeCheckable={!singleSelectMode}
    showCheckedStrategy={'SHOW_PARENT'}
    placeholder={ t('EXCP_OVERVIEW.allVersion') }
    listHeight={500}
    treeDefaultExpandedKeys={ singleSelectMode ? [intermediateValue] : intermediateValue }
    value={ intermediateValue }
    showArrow={true}
    suffixIcon={<DownOutlined />}
    // 搜索结果由索引先算好再换成一棵小树。antd 再逐个节点过滤，几十万条会把页面卡住。
    filterTreeNode={() => true}
    // 在下拉底部追加「快捷选择别名版本」区域：仅当存在带别名的版本时展示
    dropdownRender={(menu) => (
      <>
        {menu}
        {topAliasedLeafNodes.length > 0 && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <div
              style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
              // 阻止点击导致输入框失焦从而关闭下拉
              onMouseDown={(e) => e.preventDefault()}
            >
              <span style={{ marginRight: 4, color: '#888', fontSize: 12 }}>
                {ze('快捷选择：', 'Quick pick:')}
              </span>
              {topAliasedLeafNodes.map((node) => (
                <Tag
                  key={node.value}
                  color="geekblue"
                  style={{ cursor: 'pointer', marginRight: 0 }}
                  title={`${node.value} → ${node.versionAlias}`}
                  onClick={() => handleAliasQuickPick(node)}
                >
                  {node.versionAlias}
                </Tag>
              ))}
            </div>
          </>
        )}
      </>
    )}
    onChange={(v, label, extra) => {
      let newV = v;
      try {
        newV = handleChangeValue(intermediateValue, v, extra, treeSelectSearchText);
      } catch (e) { // 发生异常则走antd TreeSelect默认逻辑
        console.error('VersionHybridSelect handleChangeValue throws exception,', e);
        message.error('An error occurred when filtering options');
      }
      if (dropdownOpen) {
        setIntermediateValue(newV);
      } else {
        onChangeWhenDropdownClosed(newV);
      }
      onChange(newV);
    }}
    searchValue={treeSelectSearchText}
    onSearch={(v) => {
      setTreeSelectSearchText(v);
    }}
    onKeyDown={(e) => {
      if (e.key !== 'Enter' || singleSelectMode) {
        return;
      }
      // 可以通过按下回车键直接确认选择一个任意字串作为版本的逻辑
      const newItem = treeSelectSearchText;
      setTreeSelectSearchText('');
      if (newItem && !value.includes(newItem)) {
        const newValue = [...value, newItem];
        onChange(newValue);
        onChangeWhenDropdownClosed(newValue);
      }
    }}
    onDropdownVisibleChange={(open) => {
      setDropdownOpen(open);
      if (open) {
        setIntermediateValue(value);
      } else if (value !== intermediateValue) {
        onChangeWhenDropdownClosed(intermediateValue);
      }
      onDropdownVisibleChange(open);
    }}
    {...selectComponentSetting}
  />;
};

VersionHybridSelect.propTypes = {
  singleSelectMode: PropTypes.bool,
  treeData: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  onChange: PropTypes.func,
  onDropdownVisibleChange: PropTypes.func,
  onChangeWhenDropdownClosed: PropTypes.func,
};

export default VersionHybridSelect;
